/**
 * The file classes and load kinds an intake batch may have (IP-103; migration
 * 0012's intake_batch_file_class_check, intake_batch_load_kind_check and
 * intake_batch_kind_for_core_data), and the key each (class, kind) is
 * dispatched by - a leaf module with no imports, so the catalogue
 * (authority/catalogue.js, which validates INTAKE_UPLOAD's fileClass and
 * loadKind) and the class registry (intake/registry.js, whose parsers reach
 * the catalogue through csv.js) can both read it without a cycle.
 *
 *   NAV_VALUES, BANK_STATEMENT       no kind: keyed by the class alone
 *   CALENDAR, UNIT_PRICES, HOLDINGS,  INITIAL or DAILY, always: keyed
 *   TRANSACTIONS                      'CLASS:KIND'
 *   WM_HOLDINGS, WM_TRANSACTIONS     INITIAL or DAILY, always: keyed
 *                                    'CLASS:KIND' (migration 0026) - kept
 *                                    as uploaded, nothing loaded from them
 *   the STORED_CLASSES               no kind: keyed by the class alone
 *                                    (migration 0014) - kept as uploaded,
 *                                    nothing loaded from them
 */

/** The two classes IP-44 began with: no load kind. */
export const LEGACY_CLASSES = Object.freeze(['NAV_VALUES', 'BANK_STATEMENT']);

/** IP-103's four Core data classes: each INITIAL or DAILY. */
export const CORE_DATA_CLASSES = Object.freeze(['CALENDAR', 'UNIT_PRICES', 'HOLDINGS', 'TRANSACTIONS']);

/**
 * The store-only classes (migration 0014): the analytics files - a daily
 * benchmark, the income and expense breakdown, each fund's monthly security
 * holdings and its fund facts. Each is scanned, read as a well-formed CSV,
 * accepted by another person and kept for download; no table is loaded from
 * it and its accept sets nothing off (intake/classes/stored.js). At most 16
 * characters: intake_batch.file_class is varchar(16).
 */
export const STORED_CLASSES = Object.freeze([
  'BENCHMARK', 'INCOME_EXPENSE',
  'SECURITIES_UTDOF', 'SECURITIES_UTIF', 'SECURITIES_UTLMF', 'SECURITIES_UTOYF',
  'FUND_FACTS_UTDOF', 'FUND_FACTS_UTIF', 'FUND_FACTS_UTLMF', 'FUND_FACTS_UTOYF',
]);

/**
 * Migration 0026: the wealth-managed (WM) clients' files - their holdings and
 * transactions, each INITIAL or DAILY as a Core data file is, and kept as
 * uploaded as a STORED_CLASSES file is: no table is loaded from them yet
 * (intake/classes/stored.js). Their rows name clients, so they are client data.
 */
export const WM_CLASSES = Object.freeze(['WM_HOLDINGS', 'WM_TRANSACTIONS']);

/** Every class, in the order the File Uploads screen lists them. */
export const FILE_CLASSES = Object.freeze([...LEGACY_CLASSES, ...CORE_DATA_CLASSES, ...WM_CLASSES, ...STORED_CLASSES]);

/** INITIAL: the Core initialised from data an admin holds (as-at a past date). DAILY: today's file. */
export const LOAD_KINDS = Object.freeze(['INITIAL', 'DAILY']);

/** True when the class takes a load kind (exactly the Core data classes and the WM files). */
export function takesLoadKind(fileClass) {
  return CORE_DATA_CLASSES.includes(fileClass) || WM_CLASSES.includes(fileClass);
}

/**
 * The registry key of a batch or an upload's params: the class alone for a
 * NAV file, a statement or a store-only file, 'CLASS:KIND' for a Core data or WM file. Anything the
 * database would refuse - an unknown class, a Core data class without a
 * kind, a kind on a NAV file - has no key (null), so a caller refuses it
 * rather than guessing.
 *
 * @param {{ fileClass: string, loadKind?: string|null }} batchOrParams
 * @returns {string|null}
 */
export function registryKey({ fileClass, loadKind = null } = {}) {
  if (LEGACY_CLASSES.includes(fileClass) || STORED_CLASSES.includes(fileClass)) return loadKind === null || loadKind === undefined ? fileClass : null;
  if (takesLoadKind(fileClass) && LOAD_KINDS.includes(loadKind)) return fileClass + ':' + loadKind;
  return null;
}

/**
 * Migration 0016: the registry keys whose files the system accepts once the
 * scan validates them (intake/auto-accept.js) - the two price files, whose
 * second pair of eyes is each NAV run's approval. No person accepts or
 * rejects one. The registry entries' autoAccept flags say the same
 * (registry.test.js holds the two together); this list is here, beside the
 * kinds, so the Console reads it without the registry.
 */
export const SYSTEM_ACCEPTED_KEYS = Object.freeze(['NAV_VALUES', 'UNIT_PRICES:DAILY']);

/** Who accepts - and, when it cannot be priced, rejects - a price file the system accepted (intake_batch.accepted_by, rejected_by). */
export const SYSTEM_ACCEPTOR = 'system:auto-price';

/** True when the system, not a person, accepts a batch of this class and kind. */
export function acceptedBySystem(batchOrParams) {
  return SYSTEM_ACCEPTED_KEYS.includes(registryKey({
    fileClass: batchOrParams?.fileClass ?? batchOrParams?.file_class,
    loadKind: batchOrParams?.loadKind ?? batchOrParams?.load_kind ?? null,
  }));
}

/**
 * The registry keys no longer offered for upload, though their entries stay
 * built: a batch already uploaded is scanned, accepted and listed as before.
 * UNIT_PRICES:DAILY - today's NAV runs are made from the NAV values file, so
 * LIV's daily-unit-price is redundant; its initial load (price history) stays.
 * Read by the upload checks (intake/upload.js, FILE_CLASS_NOT_AVAILABLE) and
 * by the Console, which does not offer them.
 */
export const WITHDRAWN_KEYS = Object.freeze(['UNIT_PRICES:DAILY']);

/** True when files of this class and kind may be uploaded (not WITHDRAWN_KEYS). */
export function uploadable(params) {
  return !WITHDRAWN_KEYS.includes(registryKey(params ?? {}));
}
